import accounting from "accounting";

import { store } from "common/store";
import { getRate } from "common/helpers/accountingHelper";

const NOT_AVAILABLE = "n/a";

function formatNumber(n, precision = 0) {
    // Return n/a for empty string values
    if (n === "") {
        return NOT_AVAILABLE;
    }
    
    return accounting.formatNumber(n, precision);
}

function formatAmount(n, options = {}) {
    const format = {
        pos: "%v %s",
        neg: "(%v) %s",
        zero: "%v %s",
        ...options.format
    };
    
    // Return n/a for empty string values or null
    if (n === "" || n === null) {
        return NOT_AVAILABLE;
    }
    
    // Don't format hyphens
    if (n === "-") {
        return "-";
    }
    
    // Check if number is less than minimum precision
    const minAllowedValueByPrecision = 10 ** -options.precision;
    const ridiculouslySmallValue = 1e-10; // 0.0000000001
    
    // Make sure that we don't show less-than if the value is a very small float (e.g. 2.220446049250313e-15)
    if (n > 0 && n < minAllowedValueByPrecision && ridiculouslySmallValue < n) {
        format.pos = `&lt; ${format.pos}`;
        format.neg = `&lt; ${format.neg}`;
        n = minAllowedValueByPrecision;
    }
    
    return accounting.formatMoney(n, {
        format,
        ...options,
        symbol: options.symbol || "" // Do not render default $ when symbol is undefined
    }).trim();
}

export function getDefaultCurrentCurrency() {
    const state = store.getState();
    return {
        currency: state.common.currency,
        symbol: state.common.currency_symbol,
        decimals: state.common.currency_decimals
    };
}

export function getDefaultSymbol() {
    return getDefaultCurrentCurrency().symbol;
}

export function number(value, precision = 0) {
    return formatNumber(value, precision);
}

export function deltaNumber(value, precision = 0) {
    if (value > 0) {
        return `+${formatNumber(value, precision)}`;
    }
    return formatNumber(value, precision);
}

export function amount(value, { currency, decimals } = getDefaultCurrentCurrency()) {
    return formatAmount(value, {
        symbol: currency,
        precision: decimals
    });
}

export function cleanAmount(value, { currency, decimals } = getDefaultCurrentCurrency()) {
    return formatAmount(value, {
        precision: decimals,
        symbol: currency,
        format: { pos: "%v %s", neg: "-%v %s", zero: "%v %s" }
    });
}

export function deltaAmount(value, { currency, decimals } = getDefaultCurrentCurrency()) {
    return formatAmount(value, {
        symbol: currency,
        precision: decimals,
        format: {
            pos: "+%v %s",
            neg: "-%v %s",
            zero: "%v %s"
        }
    });
}

export function rateDisabled(value = 0, { tooltip = false } = {}) {
    if (tooltip) {
        return "<div class='tooltipped tooltipped-n' aria-label='Calculated rates are available with the Pazien Pro package'><i class='fas fa-lock'></i></div>";
    }
    return "<div class=\"pz-popover\" style=\"text-align: center\" role=\"button\" data-html=\"true\" data-toggle=\"popover\" data-trigger=\"hover\" data-placement=\"top\" data-content=\"Calculated rates are available with the Pazien Pro package\" aria-hidden=\"true\"><i class='fas fa-lock'></i></div>";
}

export function rate(value, { precision = 2 } = {}) {
    if (value === "") {
        return NOT_AVAILABLE;
    }
    
    return getRate({
        rate: value,
        precision,
        addFloatValue: false,
        humanize: true
    });
}

export function deltaRate(value, { precision = 2 } = {}) {
    if (value === "") {
        return NOT_AVAILABLE;
    }
    
    return getRate({
        rate: value,
        precision,
        addFloatValue: false,
        humanize: true,
        prefix: value > 0 ? "+" : ""
    });
}

export function abbreviateNumber(value, object = false, precision = 0) {
    const newValue = Math.abs(Math.floor(value));
    
    const lookUps = [
        { val: 1_000_000_000, suffix: "B" },
        { val: 1_000_000, suffix: "M" },
        { val: 1000, suffix: "K" }
    ];
    
    function formatReturn(val, suffix = "") {
        const obj = {
            prefix: value < 0 ? "-" : "",
            value: val,
            suffix
        };
        
        if (object) {
            return obj;
        }
        
        return `${obj.prefix}${obj.value}${obj.suffix}`;
    }
    
    function shortenNumber(num, val, forceDecimals) {
        const newNum = num / val;
        const floored = Math.floor(newNum).toString();
        if (forceDecimals) {
            const index = newNum.toString().indexOf(".");
            return index === -1 ? floored : `${floored}.${newNum.toString().substring(index + 1, index + 3)}`;
        }
        return floored.length >= 2 ? floored : (Math.round(newNum * 10) / 10).toString().substring(0, 3);
    }
    
    const match = lookUps.find((test) => newValue >= test.val);
    if (match) {
        return formatReturn(shortenNumber(newValue, match.val, precision), match.suffix);
    }
    
    return formatReturn(newValue);
}

export function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

export function formatAmountWithCurrentCurrency(n, options) {
    // Don't format hyphens
    if (n === "-") {
        return "-";
    }
    
    const state = store.getState();
    const opts = {
        format: state.config.formatting.money.format,
        precision: state.config.formatting.money.precision,
        symbol: state.common.currency_symbol,
        symbolPosition: 0, // 0=before, 1 = after
        abbreviate: false,
        ...options
    };
    
    // Allow to pick either a symbol or currency
    if (typeof opts.symbol === "function") {
        opts.symbol = opts.symbol(state.common.currency);
    }
    
    // Check if number is less than minimum precision
    const minAllowedValueByPrecision = 10 ** -opts.precision;
    const ridiculouslySmallValue = 1e-10; // 0.0000000001
    
    // Make sure that we don't show less-than if the value is a very small float (e.g. 2.220446049250313e-15)
    if (n > 0 && n < minAllowedValueByPrecision && ridiculouslySmallValue < n) {
        opts.format.pos = `&lt; ${opts.format.pos}`;
        opts.format.neg = `&lt; ${opts.format.neg}`;
        n = minAllowedValueByPrecision;
    } else if (opts.abbreviate) {
        n = abbreviateNumber(n, true, opts.precision);
        
        if (opts.symbolPosition === 1) {
            return `${n.prefix}${n.value}${n.suffix} ${opts.symbol}`;
        }
        return `${n.prefix}${opts.symbol}${n.value}${n.suffix}`;
    }
   
    return accounting.formatMoney(n, opts);
}
